import { customAlphabet } from 'nanoid';
import getConfig from 'next/config';
import { useEffect, useRef, useState } from 'react';

import { useBoolean, useIsMobile, useTheme } from '@sravni/react-utils';

import { pushEvent } from '../helpers/analytics';

interface Payload extends Record<string, any> {
  type: string;
}

const sendMessageToSpecialOffersFrontend = (iframeRef: React.RefObject<HTMLIFrameElement>, payload: Payload) => {
  iframeRef?.current?.contentWindow?.postMessage(
    {
      source: 'special-offers-iframe-wrapper',
      payload,
    },
    '*',
  );
};

const { specialOffersUrl } = getConfig().publicRuntimeConfig;
const nanoid = customAlphabet('abcdefghijklmnopqrstuvwxyz', 10);

export const useSpecialOffersVerticalWidget = (articleElement: HTMLElement, withSOWidget: boolean) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const iframeIdRef = useRef(nanoid(10));

  const [isLoaded, setLoaded] = useBoolean(false);
  const [iframeHeight, setIframeHeight] = useState<number>(0);
  const isMobile = useIsMobile();
  const [theme] = useTheme();

  useEffect(() => {
    if (!articleElement || iframeRef.current) return;

    const iframeWrapper = articleElement.querySelector('[data-special-offers]');
    if (!iframeWrapper) return;
    const tabType = iframeWrapper.getAttribute('data-special-offers');

    const iframeUrl = new URL(`${specialOffersUrl}/special-offers/vertical`);
    iframeUrl.searchParams.set('tabType', tabType);
    iframeUrl.searchParams.set('iframeId', iframeIdRef.current);

    const iframeElement = document.createElement('iframe');
    iframeElement.setAttribute('src', decodeURI(iframeUrl.toString()));
    iframeElement.setAttribute('width', '100%');
    iframeElement.setAttribute('height', `${iframeHeight}px`);
    iframeElement.setAttribute('id', iframeIdRef.current);
    iframeElement.setAttribute('data-special-offers', 'iframe');
    iframeElement.style.border = 'none';

    iframeWrapper.appendChild(iframeElement);

    iframeRef.current = iframeElement;
  }, [articleElement, iframeHeight]);

  useEffect(() => {
    iframeRef.current?.setAttribute('height', `${iframeHeight}px`);
  }, [iframeHeight]);

  useEffect(() => {
    if (!withSOWidget) return;
    const listener = ({ data }: MessageEvent) => {
      const { source, payload } = data;

      if (source !== 'special-offers') return;

      const { height, type, iframeId: iframeIdFromPayload, event } = payload;

      if (iframeIdRef.current !== iframeIdFromPayload) return;

      if (type === 'changeHeight') {
        setIframeHeight(height);
        return;
      }

      if (type === 'listenerIsAdded') {
        setLoaded.on();
        return;
      }

      if (type === 'pushEvent') {
        event && pushEvent(event);
        return;
      }
    };

    window.addEventListener('message', listener);

    return () => {
      window.removeEventListener('message', listener);
    };
  }, [setLoaded, withSOWidget]);

  useEffect(() => {
    if (!isLoaded) return;

    sendMessageToSpecialOffersFrontend(iframeRef, {
      type: 'changeIsMobile',
      isMobile,
    });
  }, [isLoaded, isMobile]);

  useEffect(() => {
    if (!isLoaded) return;

    sendMessageToSpecialOffersFrontend(iframeRef, {
      type: 'pageUrl',
      pageUrl: document.location.href,
    });
  }, [isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;
    sendMessageToSpecialOffersFrontend(iframeRef, {
      type: 'changeTheme',
      theme,
    });
  }, [isLoaded, theme]);
};
